import { useMemo, useState } from 'react';

import './styles.css';

// Fundamental accounting elements — surfaced first in the ranking
// per the pedagogy ask ("if elements meet threshold, show them
// before other accounts"). Compared by exact match to insight.question.
const ELEMENT_NAMES = new Set([
  'Assets', 'Liabilities', 'Equity', 'Revenues', 'Expenses',
]);

// Section labels accepted per tab. Anything outside these is treated
// as noise and skipped — the backend occasionally returns unrelated
// options depending on the game variant, and we don't want them in
// the ranking or the row display.
const CLASSIFICATION_SECTIONS = new Set([
  'Balance Sheet', 'Income Statement',
  'Assets', 'Liabilities', 'Equity', 'Revenues', 'Expenses',
]);
const NORMAL_SECTIONS = new Set(['Debit', 'Credit']);

const TOP_N = 10;

// Rank + inclusion rule:
// - threshold = min(10, totalValue * 0.2), matching the existing red-
//   highlight rule on the chart. totalValue = sum of count_users across
//   the account's valid options.
// - Account qualifies if its peak (largest single misclassified count)
//   is >= threshold. Peak avoids the per-student double-counting
//   ambiguity from summing across sections.
// - Ordering: elements that qualify come first (ranked by peak desc,
//   ties broken alphabetically); non-elements fill the remaining slots
//   by the same criteria. Cut at TOP_N.
function buildRanking(insights, validSections) {
  const rows = [];
  insights.forEach((insight) => {
    if (!insight?.question) return;

    const validOpts = (insight.options || []).filter(
      (o) => validSections.has(o.option)
    );
    if (validOpts.length === 0) return;

    const totalValue = validOpts.reduce((sum, o) => sum + (o.count_users || 0), 0);
    if (totalValue === 0) return;

    const threshold = Math.min(10, totalValue * 0.2);

    let home = null;
    const misclassifications = [];
    validOpts.forEach((o) => {
      const count = o.count_users || 0;
      if (o.is_correct) {
        home = o.option;
        return;
      }
      if (count > 0) {
        misclassifications.push({ label: o.option, count });
      }
    });

    if (!home) return;

    misclassifications.sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label)
    );
    const peak = misclassifications.length ? misclassifications[0].count : 0;
    if (peak < threshold) return;

    rows.push({
      account: insight.question,
      home,
      misclassifications,
      peak,
      isElement: ELEMENT_NAMES.has(insight.question),
    });
  });

  rows.sort((a, b) => {
    if (a.isElement !== b.isElement) return a.isElement ? -1 : 1;
    return b.peak - a.peak || a.account.localeCompare(b.account);
  });
  return rows.slice(0, TOP_N);
}

function AccountRow({ rank, row }) {
  return (
    <li className="misclassified-account-row">
      <span className="misclassified-account-rank">{rank}</span>
      <div className="misclassified-account-main">
        <div className="misclassified-account-name">{row.account}</div>
        <p className="misclassified-account-belongs">
          Belongs in <b>{row.home.toLowerCase()}</b>
        </p>
      </div>
      <ul className="misclassified-account-sections">
        {row.misclassifications.map((m, i) => (
          <li
            key={`${m.label}-${i}`}
            className={i === 0 ? 'misclassified-account-section is-top' : 'misclassified-account-section'}
          >
            {m.label} <span className="misclassified-account-section-count">({m.count})</span>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function TopMisclassifiedAccountsModal({
  onCloseButtonClick,
  classificationInsights = [],
  normalInsights = [],
}) {
  const [activeTab, setActiveTab] = useState('classification');

  const classificationRanking = useMemo(
    () => buildRanking(classificationInsights, CLASSIFICATION_SECTIONS),
    [classificationInsights]
  );
  const normalRanking = useMemo(
    () => buildRanking(normalInsights, NORMAL_SECTIONS),
    [normalInsights]
  );

  const rows = activeTab === 'classification' ? classificationRanking : normalRanking;

  return (
    <div className="modal-overlay" onClick={onCloseButtonClick}>
      <div
        className="modal instructor-decision-insights-modal top-misclassified-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={onCloseButtonClick} aria-label="Close" />
        <div className="top-misclassified-modal-content">
          <div className="misclassified-header">
            <div className="misclassified-header-copy">
              <h2 className="misclassified-title">Most Misclassified Accounts</h2>
              <p className="misclassified-description">
                These accounts were sorted to incorrect financial statement category.
              </p>
            </div>
            <div className="top-misclassified-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={activeTab === 'classification'}
                className={activeTab === 'classification' ? 'top-misclassified-tab is-active' : 'top-misclassified-tab'}
                onClick={() => setActiveTab('classification')}
              >
                Classification
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'normal'}
                className={activeTab === 'normal' ? 'top-misclassified-tab is-active' : 'top-misclassified-tab'}
                onClick={() => setActiveTab('normal')}
              >
                Normal
              </button>
            </div>
          </div>

          <div className="misclassified-panel">
            <div className="misclassified-colhead">
              <span className="misclassified-colhead-rank" />
              <span className="misclassified-colhead-account">Account</span>
              <span className="misclassified-colhead-sections">Misclassified Sections (Student Count)</span>
            </div>
            {rows.length > 0 ? (
              <ol className="misclassified-list">
                {rows.map((row, i) => (
                  <AccountRow key={`${activeTab}-${row.account}`} rank={i + 1} row={row} />
                ))}
              </ol>
            ) : (
              <p className="top-misclassified-empty">
                No accounts crossed the misclassification threshold for this game.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
